import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { BaseService } from 'src/app/shared/services/base.service';
import { OpportunityBoard } from 'src/app/shared/models/opportunityBoard';
import { PositionListing } from '../models/positionListing';

@Injectable()
export class OpportunityBoardService extends BaseService {
  public postionListingRemoved: Subject<string> = new Subject<string>();
  private _allBoardsArray: OpportunityBoard[] = [];
  private _allBoards: BehaviorSubject<OpportunityBoard[]> = new BehaviorSubject<OpportunityBoard[]>(
    this._allBoardsArray
  );

  public readonly allBoards: Observable<OpportunityBoard[]> = this._allBoards.asObservable();

  constructor() {
    super();
  }

  getAll(): void {
    this.get(this.opportunityBoardsBaseUrl)
      .pipe(map(this.convertOpportunityBoardData))
      .subscribe((boards) => {
        this._allBoardsArray = boards;
        this._allBoards.next(this._allBoardsArray);
      });
  }

  getById(id: string): Observable<OpportunityBoard> {
    let path = this.opportunityBoardsBaseUrl.concat('/', id);
    return this.get(path).pipe(map((data) => OpportunityBoard.parse(data)));
  }

  createOpportunityBoard(board: OpportunityBoard): Observable<OpportunityBoard> {
    let obs = this.post(this.opportunityBoardsBaseUrl, board);
    obs.pipe(map((data) => OpportunityBoard.parse(data))).subscribe((res) => {
      if (res) {
        this._allBoardsArray.push(res);
        this._allBoards.next(this._allBoardsArray);
      }
    });
    return obs;
  }

  saveQuickFilterNames(boardId: string, queryFilters: string[]): Observable<OpportunityBoard> {
    let path = this.opportunityBoardsBaseUrl.concat('/', boardId);
    let obs = this.patch(path, queryFilters);
    obs.pipe(map((data) => OpportunityBoard.parse(data))).subscribe((res) => {
      if (res) {
        this._allBoardsArray = this._allBoardsArray.map((x) => (x.id !== res.id ? x : res));
        this._allBoards.next(this._allBoardsArray);
        this.openSnackBar('Board updated', true);
      }
    });
    return obs;
  }

  updateOpportunityBoard(board: OpportunityBoard): Observable<OpportunityBoard> {
    let path = this.opportunityBoardsBaseUrl.concat('/', board.id);
    let body = JSON.stringify(board);
    let obs = this.put(path, body, null);
    obs.pipe(map((data) => OpportunityBoard.parse(data))).subscribe((res) => {
      if (res) {
        this._allBoardsArray = this._allBoardsArray.map((x) => (x.id !== res.id ? x : res));
        this._allBoards.next(this._allBoardsArray);
        this.openSnackBar('Board updated', true);
      }
    });
    return obs;
  }

  deleteOpportunityBoard(board: OpportunityBoard): void {
    let path = this.opportunityBoardsBaseUrl.concat('/', board.id);
    this.delete(path).subscribe((res) => {
      if (res) {
        this._allBoardsArray = this._allBoardsArray.filter((x) => x.id !== board.id);
        this._allBoards.next(this._allBoardsArray);
        this.router.navigate(['/admin/opportunity-boards']);
        this.openSnackBar(`${board.title} was deleted successfully`, true);
      }
    });
  }

  changeStatus(id: string): void {
    let path = this.opportunityBoardsBaseUrl.concat('/change-status/', id);
    this.patch(path)
      .pipe(map((res) => OpportunityBoard.parse(res)))
      .subscribe((res) => {
        if (res) {
          this._allBoardsArray = this._allBoardsArray.map((x) => (x.id !== res.id ? x : res));
          this._allBoards.next(this._allBoardsArray);
          this.openSnackBar(`Opportunity board status was changed successfully`, true);
        }
      });
  }

  removeListingFromBoard(boardId: string, listing: PositionListing) {
    let path = this.opportunityBoardsBaseUrl.concat(`/${boardId}/remove-listing/`, listing.id);
    this.delete(path).subscribe((res) => {
      if (res) {
        let listingTitle = listing.positionName;
        this.postionListingRemoved.next(listing.id);
        this.openSnackBar(`${listingTitle} listing removed from board`, true);
      }
    });
  }

  private get opportunityBoardsBaseUrl(): string {
    return 'opportunity-boards';
  }

  private convertOpportunityBoardData(data) {
    if (!data) {
      return [];
    }
    return data.map((board) => OpportunityBoard.parse(board));
  }
}
